/**
 * Firmware pick-list rules — which firmware images an operator is allowed to
 * aim at a given SmartLink unit.
 *
 * Each firmware image arrives with the hardware models it runs on
 * (``compatible_model_ids``), derived from its version server-side and
 * read-only here. Sending an image to a different base model can brick the
 * unit, so an image whose models exclude the target is never offered. An empty
 * list means the rule matched nothing, so compatibility is simply unknown: the
 * image stays on offer and the caller flags it. Narrowing to the unit's current
 * major release is a separate, operator-liftable concern.
 *
 * Kept free of imports so the node --test suite can import it directly.
 *
 * Contains:
 * - majorOf — leading numeric segment of a version string
 * - hasKnownModelCompatibility / isFirmwareVersionForModel — per-row rules
 * - filterFirmwareVersionOptions — the combined pick-list filter
 */

/**
 * Pull the leading numeric segment from "9.6.65" / "10.50.0".
 *
 * @param {string|number|null|undefined} version
 * @returns {number|null} null when the version doesn't start with a digit, so
 *   a missing or garbled version never accidentally passes a major filter.
 */
export const majorOf = version => {
  if (version == null) return null;
  const match = String(version).match(/^(\d+)/);
  return match ? Number(match[1]) : null;
};

/**
 * Whether the derivation resolved this row to any hardware models.
 *
 * @param {{ compatible_model_ids?: Array<number|string> }} row
 * @returns {boolean} false when the rule matched nothing for this version.
 */
export const hasKnownModelCompatibility = row =>
  Array.isArray(row?.compatible_model_ids) && row.compatible_model_ids.length > 0;

/**
 * Whether a firmware row may be offered for a unit of the given model.
 *
 * A row with no resolved models is offered (unknown, not incompatible); a
 * resolved row is offered only when it lists the model. Ids are compared as
 * strings because the catalog and the unit payload disagree on numeric vs
 * string ids.
 *
 * @param {{ compatible_model_ids?: Array<number|string> }} row
 * @param {number|string|null} modelId - Target unit's model, null when unknown.
 * @returns {boolean}
 */
export const isFirmwareVersionForModel = (row, modelId) => {
  if (modelId == null) return true;
  if (!hasKnownModelCompatibility(row)) return true;
  return row.compatible_model_ids.some(id => String(id) === String(modelId));
};

/**
 * The firmware rows an operator may pick for a unit.
 *
 * The model rule is hard — lifting the major-release restriction can never
 * reveal an image built for a different base model.
 *
 * @param {Array<object>} rows - Firmware rows (plain JS).
 * @param {object} [options]
 * @param {number|string|null} [options.modelId] - Target unit's model id.
 * @param {boolean} [options.restrictMajor] - Narrow to currentMajor.
 * @param {number|null} [options.currentMajor] - Unit's current major release.
 * @returns {Array<object>} A new array; the input is never mutated.
 */
export const filterFirmwareVersionOptions = (rows, options = {}) => {
  const { modelId = null, restrictMajor = false, currentMajor = null } = options;
  const list = Array.isArray(rows) ? rows : [];
  const forModel = modelId == null ? list.slice() : list.filter(row => isFirmwareVersionForModel(row, modelId));
  if (!restrictMajor || currentMajor == null) return forModel;
  return forModel.filter(row => majorOf(row.version) === currentMajor);
};
